import React, { useState } from 'react';
import { ArrowRightLeftIcon, ChevronDownIcon, ChevronUpIcon, EyeIcon, EyeOffIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { FormConfig, SectionConfig } from '../../types';
import { createSectionId, fieldCatalog, moveItem } from '../../utils/config';
import { SECTION_ICON_NAMES, resolveIcon } from '../../utils/icons';
import { Segmented, Select } from './AdminControls';
interface SectionsTabProps {
  config: FormConfig;
  onChange: (config: FormConfig) => void;
}
export function SectionsTab({
  config,
  onChange
}: SectionsTabProps) {
  const [expanded, setExpanded] = useState<string | null>(config.sections[0]?.id ?? null);
  const updateSection = (id: string, patch: Partial<SectionConfig>) => {
    onChange({
      ...config,
      sections: config.sections.map(s => s.id === id ? {
        ...s,
        ...patch
      } : s)
    });
  };
  const moveSection = (index: number, direction: -1 | 1) => {
    onChange({
      ...config,
      sections: moveItem(config.sections, index, direction)
    });
  };
  const addSection = () => {
    const id = createSectionId();
    onChange({
      ...config,
      sections: [...config.sections, {
        id,
        title: 'New Section',
        icon: 'ClipboardList',
        hidden: false,
        columns: 2,
        defaultCollapsed: false,
        fieldIds: []
      }]
    });
    setExpanded(id);
  };
  const deleteSection = (id: string) => {
    const removed = config.sections.find(s => s.id === id);
    const remaining = config.sections.filter(s => s.id !== id);
    if (!removed || remaining.length === 0) return;
    // Orphaned fields fall back into the first remaining section.
    remaining[0] = {
      ...remaining[0],
      fieldIds: [...remaining[0].fieldIds, ...removed.fieldIds]
    };
    onChange({
      ...config,
      sections: remaining
    });
  };
  const toggleFieldVisibility = (fieldId: string) => {
    const hidden = config.hiddenFieldIds.includes(fieldId) ? config.hiddenFieldIds.filter(f => f !== fieldId) : [...config.hiddenFieldIds, fieldId];
    onChange({
      ...config,
      hiddenFieldIds: hidden
    });
  };
  const moveField = (sectionId: string, index: number, direction: -1 | 1) => {
    const section = config.sections.find(s => s.id === sectionId);
    if (!section) return;
    updateSection(sectionId, {
      fieldIds: moveItem(section.fieldIds, index, direction)
    });
  };
  const moveFieldToSection = (fromId: string, fieldId: string, toId: string) => {
    if (fromId === toId) return;
    onChange({
      ...config,
      sections: config.sections.map(s => {
        if (s.id === fromId) return {
          ...s,
          fieldIds: s.fieldIds.filter(f => f !== fieldId)
        };
        if (s.id === toId) return {
          ...s,
          fieldIds: [...s.fieldIds, fieldId]
        };
        return s;
      })
    });
  };
  return <div className="space-y-3" data-id="element-57" data-mp="16hq4hvg0bkhc">
      <p className="text-xs leading-relaxed text-slate-500" data-id="element-58" data-mp="1ajbftccw7qpf">
        Reorder sections, rename them, move fields between them, and control which fields appear on
        the record for every user.
      </p>

      {config.sections.map((section, index) => {
      const Icon = resolveIcon(section.icon);
      const isOpen = expanded === section.id;
      const hiddenInSection = section.fieldIds.filter(f => config.hiddenFieldIds.includes(f)).length;
      return <div key={section.id} className={`overflow-hidden rounded-xl border bg-white ${section.hidden ? 'border-slate-200 opacity-60' : 'border-slate-200'}`} data-id="element-59" data-mp="2caw8ygtx1ofo" data-mpi={index}>
            <div className="flex items-center gap-2 px-3 py-2.5" data-id="element-60" data-mp="23bhi3g9u7bun">
              <div className="flex flex-col" data-id="element-61" data-mp="1ybd2rtko0n60">
                <button type="button" aria-label={`Move ${section.title} up`} disabled={index === 0} onClick={() => moveSection(index, -1)} className="text-slate-400 transition-colors hover:text-blue-600 disabled:opacity-25" data-id="element-62" data-mp="1ycod6c8m8t63">
                  <ChevronUpIcon className="h-3.5 w-3.5" data-id="element-63" data-mp="2pcnoddaqhc8o" />
                </button>
                <button type="button" aria-label={`Move ${section.title} down`} disabled={index === config.sections.length - 1} onClick={() => moveSection(index, 1)} className="text-slate-400 transition-colors hover:text-blue-600 disabled:opacity-25" data-id="element-64" data-mp="2ucs616ndod0h">
                  <ChevronDownIcon className="h-3.5 w-3.5" data-id="element-65" data-mp="2bez15yt71t0g" />
                </button>
              </div>

              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-600" data-id="element-66" data-mp="1xoc56eetih82">
                <Icon className="h-4 w-4" data-id="element-67" data-mp="21pfiou9e8164" />
              </span>

              <input value={section.title} aria-label="Section title" onChange={e => updateSection(section.id, {
            title: e.target.value
          })} className="min-w-0 flex-1 rounded-md border border-transparent px-1.5 py-1 text-sm font-semibold text-slate-800 outline-none transition-colors hover:border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-id="element-68" data-mp="1nolc8h5woaa3" />

              <button type="button" aria-label={section.hidden ? 'Show section' : 'Hide section'} onClick={() => updateSection(section.id, {
            hidden: !section.hidden
          })} className="flex h-7 w-7 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700" data-id="element-69" data-mp="1j1wzyccn8xd6">
                {section.hidden ? <EyeOffIcon className="h-4 w-4" data-id="element-70" data-mp="1ws0eeh5a7ms2" /> : <EyeIcon className="h-4 w-4" data-id="element-71" data-mp="1wss97pr21gha" />}
              </button>

              <button type="button" aria-label="Configure section" aria-expanded={isOpen} onClick={() => setExpanded(isOpen ? null : section.id)} className="flex h-7 items-center gap-1 rounded-md px-2 text-xs font-semibold text-blue-600 transition-colors hover:bg-blue-50" data-id="element-72" data-mp="2asb4hhyroxc6">
                {section.fieldIds.length}
                <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-73" data-mp="2nq961pwetp89" />
              </button>
            </div>

            {isOpen && <div className="space-y-3 border-t border-slate-100 bg-slate-50 p-3" data-id="element-74" data-mp="2wm8g9j762kpr">
                <div className="flex flex-wrap items-end gap-3" data-id="element-75" data-mp="31h4ckvjfru8o">
                  <div data-id="element-76" data-mp="36j3gc6j38t65">
                    <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-77" data-mp="36itmg7uxa1v6">
                      Columns
                    </span>
                    <Segmented ariaLabel="Section columns" value={String(section.columns)} options={[{
                value: '1',
                label: '1 col'
              }, {
                value: '2',
                label: '2 col'
              }]} onChange={v => updateSection(section.id, {
                columns: Number(v) === 1 ? 1 : 2
              })} data-id="element-78" data-mp="1c52f5f02fu2w" />
                  </div>
                  <div data-id="element-79" data-mp="3gn1n32ot41km">
                    <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-80" data-mp="3gntlel9sgdel">
                      Default state
                    </span>
                    <Segmented ariaLabel="Default state" value={section.defaultCollapsed ? 'collapsed' : 'expanded'} options={[{
                value: 'expanded',
                label: 'Expanded'
              }, {
                value: 'collapsed',
                label: 'Collapsed'
              }]} onChange={v => updateSection(section.id, {
                defaultCollapsed: v === 'collapsed'
              })} data-id="element-81" data-mp="d6l4q4qsrjlg" />
                  </div>
                  <Select label="Icon" className="w-32" value={section.icon} onChange={v => updateSection(section.id, {
              icon: v
            })} options={SECTION_ICON_NAMES.map(n => ({
              value: n,
              label: n
            }))} data-id="element-82" data-mp="wt9pb67fj3q5" />
                  {config.sections.length > 1 && <button type="button" onClick={() => deleteSection(section.id)} className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-2.5 py-2 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50" data-id="element-83" data-mp="1alg0sezn0mj9">
                      <Trash2Icon className="h-3.5 w-3.5" data-id="element-84" data-mp="1tjsaq2ydi0fw" />
                      Delete
                    </button>}
                </div>

                <div className="rounded-lg border border-slate-200 bg-white" data-id="element-85" data-mp="1ybjxs3iblo1a">
                  <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2" data-id="element-86" data-mp="3izcrhyhev8gi">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-87" data-mp="3e11ifpyxx1zc">
                      Fields
                    </span>
                    {hiddenInSection > 0 && <span className="text-[11px] font-medium text-slate-400" data-id="element-88" data-mp="3ns5xepom8mu6">
                        {hiddenInSection} hidden
                      </span>}
                  </div>
                  <ul className="max-h-72 overflow-y-auto" data-id="element-89" data-mp="2v4jnkzosolyv">
                    {section.fieldIds.length === 0 && <li className="px-3 py-6 text-center text-xs text-slate-400" data-id="element-90" data-mp="39058ovrwqlm0">
                        No fields yet — move fields here from another section.
                      </li>}
                    {section.fieldIds.map((fieldId, fieldIndex) => {
                const field = fieldCatalog[fieldId];
                if (!field) return null;
                const isHidden = config.hiddenFieldIds.includes(fieldId);
                return <li key={fieldId} className="group flex items-center gap-1.5 border-b border-slate-50 px-2 py-1.5 last:border-0" data-id="element-91" data-mp="39dq0jg59k21z" data-mpi={fieldIndex}>
                          <div className="flex flex-col" data-id="element-92" data-mp="2zfaazhlvx0ul">
                            <button type="button" aria-label={`Move ${field.label} up`} disabled={fieldIndex === 0} onClick={() => moveField(section.id, fieldIndex, -1)} className="text-slate-300 transition-colors hover:text-blue-600 disabled:opacity-25" data-id="element-93" data-mp="2zgtqh5zpv7vb">
                              <ChevronUpIcon className="h-3 w-3" data-id="element-94" data-mp="jonq9tzmi0v3" />
                            </button>
                            <button type="button" aria-label={`Move ${field.label} down`} disabled={fieldIndex === section.fieldIds.length - 1} onClick={() => moveField(section.id, fieldIndex, 1)} className="text-slate-300 transition-colors hover:text-blue-600 disabled:opacity-25" data-id="element-95" data-mp="r86t5ksduoul">
                              <ChevronDownIcon className="h-3 w-3" data-id="element-96" data-mp="8b5jvnrsq2ku" />
                            </button>
                          </div>

                          <span className={`min-w-0 flex-1 truncate text-xs ${isHidden ? 'text-slate-400 line-through' : 'text-slate-700'}`} data-id="element-97" data-mp="i6pd22ez6n5w">
                            {field.label}
                          </span>

                          {config.sections.length > 1 && <div className="relative" data-id="element-98" data-mp="3uod9381klkz9">
                              <select aria-label={`Move ${field.label} to another section`} value="" onChange={e => moveFieldToSection(section.id, fieldId, e.target.value)} className="h-6 w-6 cursor-pointer appearance-none rounded-md text-transparent opacity-0" data-id="element-99" data-mp="3umsczdzlxtza">
                                <option value="" data-id="element-100" data-mp="370lxk6nutbz1">Move to…</option>
                                {config.sections.filter(s => s.id !== section.id).map(s => <option key={s.id} value={s.id} data-id="element-101" data-mp="2sa4vqm7b4k1m">
                                      {s.title}
                                    </option>)}
                              </select>
                              <ArrowRightLeftIcon className="pointer-events-none absolute inset-0 m-auto h-3.5 w-3.5 text-slate-300 group-hover:text-slate-500" data-id="element-102" data-mp="12sr9hepe6xul" />
                            </div>}

                          <button type="button" aria-label={isHidden ? `Show ${field.label}` : `Hide ${field.label}`} onClick={() => toggleFieldVisibility(fieldId)} className={`flex h-6 w-6 items-center justify-center rounded-md transition-colors ${isHidden ? 'text-slate-400 hover:bg-slate-100' : 'text-blue-500 hover:bg-blue-50'}`} data-id="element-103" data-mp="ttvzdrj893m0">
                            {isHidden ? <EyeOffIcon className="h-3.5 w-3.5" data-id="element-104" data-mp="62qlvj0renev" /> : <EyeIcon className="h-3.5 w-3.5" data-id="element-105" data-mp="lcdk3baclg3i" />}
                          </button>
                        </li>;
              })}
                  </ul>
                </div>
              </div>}
          </div>;
    })}

      <button type="button" onClick={addSection} className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-slate-300 py-3 text-sm font-semibold text-slate-500 transition-colors hover:border-blue-400 hover:text-blue-600" data-id="element-106" data-mp="7l8m0dmmovva">
        <PlusIcon className="h-4 w-4" data-id="element-107" data-mp="vab41ml6cqqb" />
        Add section
      </button>
    </div>;
}